import { WEBSITE_URL } from "../lib/env";

export const SITE_NAME = "ApexBrain";
export const SITE_TAGLINE = "Unified Intelligence Layer";
export const TWITTER_HANDLE = "@apexbrain";

/** Absolute production URL for Open Graph / WhatsApp / social crawlers. */
export const PRODUCTION_ORIGIN = "https://apexbrain.in";

/** Social preview image — must be absolute HTTPS; crawlers do not run JS. */
export const OG_IMAGE = {
    path: "/og-image.png",
    width: 1200,
    height: 650,
    type: "image/png",
    alt: `${SITE_NAME} — ${SITE_TAGLINE}`,
};

export const DEFAULT_OG_IMAGE = `${PRODUCTION_ORIGIN}${OG_IMAGE.path}`;

/** Favicon & device icons (paths from site root). */
export const SITE_ICONS = {
    faviconIco: "/favicon.ico",
    favicon16: "/favicon-16x16.png",
    favicon32: "/favicon-32x32.png",
    appleTouchIcon: "/apple-touch-icon.png",
    appleTouchIconSizes: "180x180",
    android192: "/icon-192x192.png",
    android512: "/icon-512x512.png",
    /** Duplicate paths under /icons/ for backwards compatibility */
    iconsDir: {
        faviconIco: "/icons/favicon.ico",
        favicon16: "/icons/favicon-16x16.png",
        favicon32: "/icons/favicon-32x32.png",
        appleTouchIcon: "/icons/apple-touch-icon-old.png",
        android192: "/icons/icon-192x192.png",
        android512: "/icons/icon-512x512.png",
    },
    manifest: "/manifest.json",
    /** Square logo for JSON-LD / structured data */
    logoSquare: "/icon-512x512.png",
};

export const absoluteAssetUrl = (path) =>
    `${PRODUCTION_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;

const DEFAULT_KEYWORDS = [
    "ApexBrain",
    "unified business operating system",
    "all-in-one team OS",
    "team operating system",
    "CRM",
    "HRMS",
    "project management",
    "sales pipeline",
    "quotations",
    "attendance",
    "leave management",
    "team collaboration",
    "business automation",
    "India SaaS",
].join(", ");

export const SITE_DEFAULTS = {
    title: `${SITE_NAME} — ${SITE_TAGLINE}`,
    description:
        "ApexBrain is the unified intelligence layer for teams that ship — sales, HR, projects, finance, documents, and communications in one connected OS.",
    keywords: DEFAULT_KEYWORDS,
    ogImage: DEFAULT_OG_IMAGE,
    ogImageWidth: OG_IMAGE.width,
    ogImageHeight: OG_IMAGE.height,
    ogImageType: OG_IMAGE.type,
    ogImageAlt: OG_IMAGE.alt,
    ogType: "website",
    twitterCard: "summary_large_image",
    noindex: false,
};

/** @type {Record<string, Partial<typeof SITE_DEFAULTS>>} */
export const ROUTE_SEO = {
    "/": {
        title: `${SITE_NAME} — ${SITE_TAGLINE} | The OS for Teams That Ship`,
        description:
            "Replace scattered tools with one connected operating system. ApexBrain unifies sales, HR, projects, finance, documents, and team communications with AI built in.",
        keywords:
            "ApexBrain, unified business platform, all-in-one team OS, sales CRM, HRMS, project management, business automation",
        ogType: "website",
    },
    "/about": {
        title: `About ${SITE_NAME} — Built for Teams That Ship`,
        description:
            "Learn how ApexBrain helps growing teams replace tool sprawl with a single intelligence layer — built by .",
        keywords: "about ApexBrain, Warx Technovation, team operating system, company mission",
    },
    "/pricing": {
        title: `Pricing — ${SITE_NAME} Plans & Modules`,
        description:
            "Transparent yearly pricing for ApexBrain modules — sales, HR, projects, finance, documents, communications, analytics, and AI automation.",
        keywords: "ApexBrain pricing, SaaS pricing India, business software plans, yearly subscription",
    },
    "/pricing/payment/success": {
        title: `Payment Successful — ${SITE_NAME}`,
        description: "Your ApexBrain payment was processed successfully.",
        noindex: true,
    },
    "/pricing/payment/failure": {
        title: `Payment Failed — ${SITE_NAME}`,
        description: "Your ApexBrain payment could not be completed.",
        noindex: true,
    },
    "/pricing/payment/result": {
        title: `Payment Status — ${SITE_NAME}`,
        description: "ApexBrain payment result.",
        noindex: true,
    },
    "/faqs": {
        title: `FAQs — ${SITE_NAME}`,
        description:
            "Answers to common questions about ApexBrain — setup, modules, billing, security, integrations, and getting your team onboarded.",
        keywords: "ApexBrain FAQ, help, support questions, onboarding",
    },
    "/policies": {
        title: `Policies — ${SITE_NAME} Legal & Privacy`,
        description:
            "ApexBrain privacy policy, terms of service, refund policy, and legal information. ",
        keywords: "ApexBrain privacy policy, terms of service, legal, GDPR",
    },
    "/case-studies": {
        title: `Case Studies — ${SITE_NAME} Customer Stories`,
        description:
            "See how teams use ApexBrain to unify sales, HR, and operations — real outcomes from faster closes to cleaner month-end close.",
        keywords: "ApexBrain case studies, customer success, business outcomes",
    },
    "/docs": {
        title: `Documentation — ${SITE_NAME}`,
        description:
            "Product documentation, guides, and references for getting the most out of ApexBrain across every module.",
        keywords: "ApexBrain docs, documentation, user guide, API",
    },
    "/startups": {
        title: `For Startups — ${SITE_NAME}`,
        description:
            "ApexBrain for startups — one OS to run sales, people, and delivery without juggling a dozen subscriptions from day one.",
        keywords: "startup software, ApexBrain startups, early-stage team tools",
    },
    "/use-cases": {
        title: `Use Cases — ${SITE_NAME}`,
        description:
            "Explore ApexBrain use cases across sales teams, HR ops, project delivery, finance, and cross-functional leadership.",
        keywords: "ApexBrain use cases, workflows, business scenarios",
    },
    "/compliance": {
        title: `Compliance — ${SITE_NAME}`,
        description:
            "How ApexBrain supports compliance-ready operations — data handling, access controls, and audit-friendly workflows.",
        keywords: "ApexBrain compliance, data protection, audit, security compliance",
    },
    "/help": {
        title: `Help Center — ${SITE_NAME}`,
        description:
            "Get help with ApexBrain — troubleshooting, account setup, module guides, and contact options for your team.",
        keywords: "ApexBrain help, support center, troubleshooting",
    },
    "/compare": {
        title: `Compare — ${SITE_NAME} vs Jira, Monday, ClickUp`,
        description:
            "Compare ApexBrain modules and features against Jira, Monday.com, and ClickUp — see the unified alternative to tool sprawl.",
        keywords: "ApexBrain vs Jira, vs Monday, vs ClickUp, software comparison",
    },
    "/blog": {
        title: `Blog — ${SITE_NAME} Insights & Updates`,
        description:
            "Product updates, operating playbooks, and team intelligence insights from the ApexBrain team.",
        keywords: "ApexBrain blog, product updates, team operations, SaaS insights",
        ogType: "website",
    },
    "/changelog": {
        title: `Changelog — ${SITE_NAME} Release Notes`,
        description: "What's new in ApexBrain — feature releases, improvements, and fixes across every module.",
        keywords: "ApexBrain changelog, release notes, product updates",
    },
    "/roadmap": {
        title: `Roadmap — ${SITE_NAME} What's Next`,
        description:
            "The ApexBrain product roadmap — upcoming modules, AI capabilities, and platform improvements on the horizon.",
        keywords: "ApexBrain roadmap, product plans, upcoming features",
    },
    "/security": {
        title: `Security — ${SITE_NAME}`,
        description:
            "ApexBrain security practices — encryption, access control, infrastructure, and how we protect your team's data.",
        keywords: "ApexBrain security, data encryption, SaaS security, SOC",
    },
    "/careers": {
        title: `Careers — Join ${SITE_NAME}`,
        description:
            "Build the unified intelligence layer with us. Explore open roles at ApexBrain and Warx Technovation.",
        keywords: "ApexBrain careers, jobs, Warx Technovation hiring",
    },
    "/contact-sales": {
        title: `Contact Sales — ${SITE_NAME}`,
        description:
            "Talk to the ApexBrain team about demos, enterprise plans, and rolling out the OS across your organization.",
        keywords: "contact ApexBrain sales, demo request, enterprise",
    },
    "/status": {
        title: `System Status — ${SITE_NAME}`,
        description: "Live status and uptime for ApexBrain services and platform components.",
        keywords: "ApexBrain status, uptime, system health",
    },
};

/** Paths included in sitemap.xml (excludes noindex / transactional routes). */
export const SITEMAP_PATHS = [
    "/",
    "/about",
    "/pricing",
    "/faqs",
    "/policies",
    "/case-studies",
    "/docs",
    "/startups",
    "/use-cases",
    "/compliance",
    "/help",
    "/compare",
    "/blog",
    "/changelog",
    "/roadmap",
    "/security",
    "/careers",
    "/contact-sales",
];

export function normalizePath(pathname) {
    if (!pathname || pathname === "/") return "/";
    return pathname.replace(/\/+$/, "") || "/";
}

export function getSeoForPath(pathname, overrides = {}) {
    const path = normalizePath(pathname);

    let routeSeo = ROUTE_SEO[path];
    if (!routeSeo && path.startsWith("/blog/") && path !== "/blog") {
        routeSeo = {
            title: `Blog — ${SITE_NAME}`,
            description: SITE_DEFAULTS.description,
            keywords: "ApexBrain blog, article",
            ogType: "article",
        };
    }

    const merged = {
        ...SITE_DEFAULTS,
        ...routeSeo,
        ...overrides,
        canonical:
            overrides.canonical ||
            `${WEBSITE_URL}${path === "/" ? "" : path}`,
        ogImage: overrides.ogImage || routeSeo?.ogImage || SITE_DEFAULTS.ogImage,
        ogImageWidth:
            overrides.ogImageWidth ||
            routeSeo?.ogImageWidth ||
            SITE_DEFAULTS.ogImageWidth,
        ogImageHeight:
            overrides.ogImageHeight ||
            routeSeo?.ogImageHeight ||
            SITE_DEFAULTS.ogImageHeight,
        ogImageType:
            overrides.ogImageType ||
            routeSeo?.ogImageType ||
            SITE_DEFAULTS.ogImageType,
        ogImageAlt:
            overrides.ogImageAlt ||
            routeSeo?.ogImageAlt ||
            SITE_DEFAULTS.ogImageAlt,
    };

    if (!merged.keywords) {
        merged.keywords = SITE_DEFAULTS.keywords;
    }

    return merged;
}

export function getOrganizationJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: PRODUCTION_ORIGIN,
        logo: absoluteAssetUrl(SITE_ICONS.logoSquare),
        description: SITE_DEFAULTS.description,
        sameAs: [],
        parentOrganization: {
            "@type": "Organization",
            name: "Warx Technovation Pvt Ltd",
        },
    };
}

export function getArticleJsonLd({ title, description, url, datePublished, author }) {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        url,
        image: DEFAULT_OG_IMAGE,
        datePublished: datePublished || undefined,
        author: author
            ? { "@type": "Person", name: author }
            : { "@type": "Organization", name: SITE_NAME },
        publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: {
                "@type": "ImageObject",
                url: absoluteAssetUrl(SITE_ICONS.logoSquare),
            },
        },
    };
}
